import { Controller } from '@hotwired/stimulus'
import Chart from 'chart.js/auto'

export default class extends Controller {
  static targets = [
    'loading',
    'ageCanvas', 'genderCanvas', 'goalsCanvas', 'activityCanvas',
    'wearablesCanvas', 'appsCanvas', 'dietCanvas', 'unitCanvas',
    'totalProfiles', 'onboardingRate', 'avgCompleteness', 'avgAge', 'totalUsers'
  ]

  static values = {
    url: String,
    startDate: String,
    endDate: String
  }

  connect() {
    this.charts = {}
    this.isLoading = false
    this.loadData()
  }

  disconnect() {
    this.destroyAllCharts()
  }

  destroyAllCharts() {
    Object.keys(this.charts).forEach((name) => {
      if (this.charts[name]) {
        this.charts[name].destroy()
        this.charts[name] = null
      }
    })
    this.charts = {}

    // Also clear any "no data" messages and restore canvases
    this.element.querySelectorAll('.no-data-message').forEach((el) => el.remove())
    this.element.querySelectorAll('canvas').forEach((canvas) => {
      canvas.style.display = ''
    })
  }

  updateDateRange(event) {
    const { startDate, endDate } = event.detail
    this.startDateValue = startDate
    this.endDateValue = endDate
    this.loadData()
  }

  async loadData() {
    if (this.isLoading) return
    this.isLoading = true
    this.showLoading()

    try {
      const params = new URLSearchParams({
        start_date: this.startDateValue,
        end_date: this.endDateValue
      })

      const response = await fetch(`${this.urlValue}?${params}`)
      const data = await response.json()

      // Destroy existing charts before creating new ones
      this.destroyAllCharts()

      this.updateStats(data)
      this.updateCharts(data)
    } catch (error) {
      console.error('Failed to load demographics data:', error)
    } finally {
      this.isLoading = false
      this.hideLoading()
    }
  }

  updateStats(data) {
    const summary = data.summary || {}

    if (this.hasTotalProfilesTarget) {
      this.totalProfilesTarget.textContent = this.formatNumber(summary.total_profiles || 0)
    }
    if (this.hasOnboardingRateTarget) {
      this.onboardingRateTarget.textContent = `${summary.onboarding_completion_rate || 0}%`
    }
    if (this.hasAvgCompletenessTarget) {
      this.avgCompletenessTarget.textContent = `${summary.avg_completeness || 0}%`
    }
    if (this.hasAvgAgeTarget) {
      this.avgAgeTarget.textContent = summary.avg_age || '-'
    }
    if (this.hasTotalUsersTarget) {
      this.totalUsersTarget.textContent = this.formatNumber(summary.total_users || 0)
    }
  }

  updateCharts(data) {
    // Age distribution - vertical bar chart
    this.renderVerticalBarChart('age', data.age_distribution, 'Users')

    // Gender breakdown - doughnut chart
    this.renderDoughnutChart('gender', data.gender_breakdown)

    // Fitness goals - horizontal bar chart
    this.renderHorizontalBarChart('goals', data.fitness_goals, 'Users')

    // Activity levels - vertical bar chart
    this.renderVerticalBarChart('activity', data.activity_levels, 'Users')

    // Wearables - horizontal bar chart
    this.renderHorizontalBarChart('wearables', data.wearables_adoption, 'Users')

    // Apps - horizontal bar chart
    this.renderHorizontalBarChart('apps', data.apps_adoption, 'Users')

    // Diet types - doughnut chart
    this.renderDoughnutChart('diet', data.diet_types)

    // Unit system - pie chart
    this.renderPieChart('unit', data.unit_systems)
  }

  renderVerticalBarChart(name, chartData, label) {
    const canvas = this[`${name}CanvasTarget`]
    if (!canvas) return

    if (this.charts[name]) {
      this.charts[name].destroy()
    }

    this.charts[name] = new Chart(canvas, {
      type: 'bar',
      data: {
        labels: chartData?.labels || [],
        datasets: [{
          label: label,
          data: chartData?.data || [],
          backgroundColor: 'rgba(15, 23, 42, 0.8)',
          borderColor: 'rgb(15, 23, 42)',
          borderWidth: 2
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false }
        },
        scales: {
          x: {
            grid: { display: false }
          },
          y: {
            beginAtZero: true,
            grid: { color: 'rgba(0,0,0,0.05)' }
          }
        }
      }
    })
  }

  renderHorizontalBarChart(name, chartData, label) {
    const canvas = this[`${name}CanvasTarget`]
    if (!canvas) return

    if (this.charts[name]) {
      this.charts[name].destroy()
    }

    // Show "no data" message for empty charts
    if (!chartData?.labels?.length) {
      this.showNoData(canvas)
      return
    }

    this.charts[name] = new Chart(canvas, {
      type: 'bar',
      data: {
        labels: chartData?.labels || [],
        datasets: [{
          label: label,
          data: chartData?.data || [],
          backgroundColor: 'rgba(15, 23, 42, 0.8)',
          borderColor: 'rgb(15, 23, 42)',
          borderWidth: 2
        }]
      },
      options: {
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false }
        },
        scales: {
          x: {
            beginAtZero: true,
            grid: { color: 'rgba(0,0,0,0.05)' }
          },
          y: {
            grid: { display: false }
          }
        }
      }
    })
  }

  renderDoughnutChart(name, chartData) {
    const canvas = this[`${name}CanvasTarget`]
    if (!canvas) return

    if (this.charts[name]) {
      this.charts[name].destroy()
    }

    // Show "no data" message for empty charts
    if (!chartData?.labels?.length) {
      this.showNoData(canvas)
      return
    }

    const colors = this.generateColors(chartData?.labels?.length || 0)

    this.charts[name] = new Chart(canvas, {
      type: 'doughnut',
      data: {
        labels: chartData?.labels || [],
        datasets: [{
          data: chartData?.data || [],
          backgroundColor: colors.backgrounds,
          borderColor: colors.borders,
          borderWidth: 2
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'right',
            labels: {
              boxWidth: 12,
              font: { size: 11 }
            }
          }
        }
      }
    })
  }

  renderPieChart(name, chartData) {
    const canvas = this[`${name}CanvasTarget`]
    if (!canvas) return

    if (this.charts[name]) {
      this.charts[name].destroy()
    }

    const colors = this.generateColors(chartData?.labels?.length || 0)

    this.charts[name] = new Chart(canvas, {
      type: 'pie',
      data: {
        labels: chartData?.labels || [],
        datasets: [{
          data: chartData?.data || [],
          backgroundColor: colors.backgrounds,
          borderColor: colors.borders,
          borderWidth: 2
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'right',
            labels: {
              boxWidth: 12,
              font: { size: 11 }
            }
          }
        }
      }
    })
  }

  generateColors(count) {
    const palette = [
      { bg: 'rgba(15, 23, 42, 0.8)', border: 'rgb(15, 23, 42)' }, // slate-900
      { bg: 'rgba(71, 85, 105, 0.8)', border: 'rgb(71, 85, 105)' }, // slate-600
      { bg: 'rgba(148, 163, 184, 0.8)', border: 'rgb(148, 163, 184)' }, // slate-400
      { bg: 'rgba(203, 213, 225, 0.8)', border: 'rgb(203, 213, 225)' }, // slate-300
      { bg: 'rgba(30, 58, 138, 0.8)', border: 'rgb(30, 58, 138)' }, // blue-900
      { bg: 'rgba(29, 78, 216, 0.8)', border: 'rgb(29, 78, 216)' }, // blue-700
      { bg: 'rgba(59, 130, 246, 0.8)', border: 'rgb(59, 130, 246)' }, // blue-500
      { bg: 'rgba(147, 197, 253, 0.8)', border: 'rgb(147, 197, 253)' } // blue-300
    ]

    const backgrounds = []
    const borders = []

    for (let i = 0; i < count; i += 1) {
      const color = palette[i % palette.length]
      backgrounds.push(color.bg)
      borders.push(color.border)
    }

    return { backgrounds, borders }
  }

  formatNumber(num) {
    return new Intl.NumberFormat().format(num)
  }

  showLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.remove('hidden')
    }
  }

  hideLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.add('hidden')
    }
  }

  showNoData(canvas) {
    // Hide canvas and show "no data" message
    canvas.style.display = 'none'

    // Check if message already exists
    const parent = canvas.parentElement
    if (parent.querySelector('.no-data-message')) return

    const message = document.createElement('div')
    message.className = 'no-data-message flex items-center justify-center h-full text-slate-400 text-xs uppercase tracking-wider'
    message.textContent = 'NO_DATA_AVAILABLE'
    parent.appendChild(message)
  }
}
